import { useState } from 'react';
import stylex from '@stylexjs/stylex';
import { trim } from 'lodash';
import stripAnsi from 'strip-ansi';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiPipelineResponseBody } from '~/lib/api/modules/Builds';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';

export type IPipelineErrorProps = IContainerProps & {
  pipeline: IApiPipelineResponseBody;
  maxLines?: number;
};

const styles = stylex.create({
  host: {},
  errorLines: (initialLine?: number) => ({
    counterReset: `line ${initialLine ?? '0'}`,
    paddingLeft: '2rem',
  }),
  errorLine: {
    '::before': {
      display: 'inline-block',
      content: '"" counter(line, decimal-leading-zero) "  "',
      counterIncrement: 'line',
      width: '1rem',
    },
    textIndent: '-1rem',
  },
  expandButton: {
    fontWeight: 'bold',
  },
});

export const PipelineError: React.FC<IPipelineErrorProps> = (props) => {
  const { sx, pipeline, maxLines } = props;
  const [errorExpanded, setErrorExpanded] = useState(false);
  const errorLines = stripAnsi(trim(pipeline.current.error)).split('\n');
  const canExpand = maxLines !== undefined && errorLines.length > maxLines;
  const hasMore = !errorExpanded && maxLines !== undefined;

  return errorLines ? (
    <ErrorMessage
      sx={sx}
      error={`A fatal error occurred during the build process (${pipeline.current.step}):`}
    >
      {canExpand ? (
        <button
          {...stylex.props(styles.expandButton)}
          onClick={() => setErrorExpanded((expanded) => !expanded)}
        >
          {errorExpanded ? 'Collapse' : `Show all ${errorLines.length} lines`}
        </button>
      ) : null}

      <ol
        {...stylex.props(
          styles.errorLines(
            hasMore
              ? errorLines.length - maxLines - (maxLines > 1 ? 1 : 0)
              : undefined,
          ),
        )}
      >
        {hasMore && maxLines > 1 ? (
          <li {...stylex.props(styles.errorLine)}>(...)</li>
        ) : null}

        {errorLines.slice(hasMore ? -maxLines : 0).map((line, lineIndex) => (
          <li key={lineIndex} {...stylex.props(styles.errorLine)}>
            {line}
          </li>
        ))}
      </ol>
    </ErrorMessage>
  ) : null;
};
